/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useNavigate } from 'react-router-dom';
import { type FC, useEffect } from 'react';

import classNames from 'classnames';
import {
  AbrSpaceIcon,
  IconAbrLibrary,
  IconAbrLibraryFill,
  IconAbrAppStore,
  IconAbrAppStoreFill,
  IconAbrWorkbench,
  IconAbrWorkbenchFill,
  setWorkspaceRoutePrefix,
} from '@coze-studio/abr-brand';
import { useSpaceStore } from '@coze-foundation/space-store-adapter';
import { localStorageService } from '@coze-foundation/local-storage';
import { I18n } from '@coze-arch/i18n';
import { useRouteConfig } from '@coze-arch/bot-hooks';
import { SpaceType } from '@coze-arch/bot-api/developer_api';
import { IconCozDocument } from '@coze-arch/coze-design/icons';

import { resolveWorkspaceSpaceId } from '../utils/resolve-workspace-space-id';
import {
  ABR_ROUTE_PREFIX,
  ABR_APP_STORE_SUB_MENU_KEY,
  ABR_APP_STORE_PATH,
  ABR_CODING_SESSIONS_SUB_MENU_KEY,
  ABR_CODING_SESSIONS_PATH,
  AbrSpaceSubModuleEnum,
} from '../constants';

import styles from './abr-workspace-sub-menu.module.less';

export const AbrWorkspaceSubMenu: FC = () => {
  const navigate = useNavigate();
  const { subMenuKey } = useRouteConfig();
  const currentSpace = useSpaceStore(state => state.space);
  const spaceId = currentSpace?.id;
  const isPersonalSpace = currentSpace?.space_type === SpaceType.Personal;
  const spaceDisplayName = isPersonalSpace
    ? I18n.t('menu_title_personal_space')
    : currentSpace?.name || I18n.t('abr_home_unknown');

  const subMenu = [
    {
      icon: <IconAbrWorkbench size={18} />,
      activeIcon: <IconAbrWorkbenchFill size={18} />,
      title: I18n.t('abr_shell_menu_workbench'),
      path: AbrSpaceSubModuleEnum.DEVELOP,
      dataTestId: 'abr-navigation-workbench',
    },
    {
      icon: <IconAbrLibrary size={18} />,
      activeIcon: <IconAbrLibraryFill size={18} />,
      title: I18n.t('abr_shell_menu_library'),
      path: AbrSpaceSubModuleEnum.LIBRARY,
      dataTestId: 'abr-navigation-library',
    },
    {
      icon: <IconCozDocument />,
      activeIcon: <IconCozDocument />,
      title: I18n.t('abr_shell_menu_document_workspace'),
      path: AbrSpaceSubModuleEnum.DOCUMENT_WORKSPACE,
      dataTestId: 'abr-navigation-document-workspace',
    },
    {
      icon: <IconAbrAppStore size={18} />,
      activeIcon: <IconAbrAppStoreFill size={18} />,
      title: I18n.t('abr_shell_menu_app_store'),
      path: ABR_APP_STORE_SUB_MENU_KEY,
      dataTestId: 'abr-navigation-app-store',
      iconClassName: styles.icoAppStore,
    },
    {
      icon: <IconAbrLibrary size={18} />,
      activeIcon: <IconAbrLibraryFill size={18} />,
      title: I18n.t('abr_shell_menu_coding_sessions'),
      path: ABR_CODING_SESSIONS_SUB_MENU_KEY,
      dataTestId: 'abr-navigation-coding-sessions',
    },
  ];

  const handleNav = async (path: string) => {
    if (path === ABR_APP_STORE_SUB_MENU_KEY) {
      navigate(ABR_APP_STORE_PATH);
      return;
    }
    if (path === ABR_CODING_SESSIONS_SUB_MENU_KEY) {
      navigate(ABR_CODING_SESSIONS_PATH);
      return;
    }
    const resolvedSpaceId = spaceId ?? (await resolveWorkspaceSpaceId());
    if (!resolvedSpaceId) {
      return;
    }
    localStorageService.setValue('workspace-spaceId', resolvedSpaceId);
    localStorageService.setValue('workspace-subMenu', path);
    setWorkspaceRoutePrefix(ABR_ROUTE_PREFIX);
    navigate(`${ABR_ROUTE_PREFIX}/space/${resolvedSpaceId}/${path}`);
  };

  useEffect(() => {
    if (!spaceId) {
      void resolveWorkspaceSpaceId();
    }
  }, [spaceId]);

  return (
    <aside className={styles.sidebar}>
      <div className={styles.spaceBlock}>
        <AbrSpaceIcon
          className={styles.spaceIcon}
          iconUrl={currentSpace?.icon_url}
          size={28}
        />
        <div className={styles.spaceName}>
          {spaceDisplayName}
          {!isPersonalSpace ? (
            <span>{I18n.t('abr_shell_workspace_label')}</span>
          ) : null}
        </div>
      </div>
      <div className={styles.navSection}>
        {I18n.t('abr_shell_nav_agent_center')}
      </div>
      {subMenu.map(item => {
        const isActive = subMenuKey === item.path;
        return (
          <div
            key={item.path}
            className={classNames(styles.navItem, isActive && styles.active)}
            onClick={() => handleNav(item.path)}
            data-testid={item.dataTestId}
          >
            <span className={classNames(styles.ico, item.iconClassName)}>
              {isActive ? item.activeIcon : item.icon}
            </span>
            <span className={styles.label}>{item.title}</span>
          </div>
        );
      })}
    </aside>
  );
};
